<template>
  <div class="wrapper">
    <UiToastList />
    <div class="main k-login">
      <div class="row">
        <div class="col-lg-6 col-md-6 col-sm-12">
          <div class="kl-inr-1 kl--container">
            <div class="kl-top heading-kl">
              <h3>Enter OTP</h3>
            </div>

            <form class="login-page-form" @submit.prevent="verifyOtp">
              <!-- Info -->
              <p class="mb-3 otp-info heading-info">
                We sent a 6-digit code to <strong>{{ emailMasked }}</strong>.
              </p>

              <!-- OTP BOXES -->
              <div class="otp-box-container">
                <input v-for="(digit, index) in otpBoxes" :key="index" ref="otpRefs" type="text" maxlength="1"
                  class="otp-box" v-model="otpBoxes[index]" @input="handleInput($event, index)"
                  @keydown.backspace="handleBackspace($event, index)" @paste="handlePaste($event)" />
              </div>

              <p class="field-error" v-if="fieldErrors.otp">
                {{ fieldErrors.otp }}
              </p>

              <!-- Submit -->
              <button type="submit" class="login-submit-button login-primary-button"
                :disabled="isLoading || otp.length !== 6">
                Verify OTP
                <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
              </button>

              <!-- Resend + Change Email -->
              <div class="otp-footer">
                <button type="button" class="otp-resend" @click="resendOtp" :disabled="resendLoading || timer > 0">
                  <span v-if="resendLoading" class="spinner-border spinner-border-sm"></span>

                  <template v-if="timer > 0"> Resend in {{ timer }}s </template>

                  <template v-else> Resend OTP </template>
                </button>

                <span class="otp-divider">|</span>

                <router-link class="otp-change-email" :to="{ name: 'KioskLogin' }">
                  Change Email
                </router-link>
              </div>
            </form>
          </div>
        </div>

        <div class="col-lg-6 col-md-6 col-sm-12 col-r-kl">
          <div class="kl-inr-2">
            <img src="@/assets/img/mamabots-logo.png" width="450" class="mb-5" />
            <h1>Welcome To MAMABOTS</h1>
            <p>Dont Have An Account?</p>
            <router-link to="/create-account">
              <button>Sign Up</button>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import UiToastList from "@/components/UiToastList";
import { AuthService } from "@/services/auth.service";
import { handleError } from "@/services/util";
import { lowercaseString } from "@/utils/helpers";

export default {
  name: "KioskOtp",
  components: { UiToastList },

  data() {
    return {
      email: "",
      otpBoxes: ["", "", "", "", "", ""], // ⬅ six-box OTP
      fieldErrors: {},
      isLoading: false,
      resendLoading: false,
      timer: 60,
      timerInterval: null,
    };
  },

  computed: {
    otp() {
      return this.otpBoxes.join("");
    },

    emailMasked() {
      if (!this.email) return "";
      const parts = this.email.split("@");
      const name = parts[0];
      return name.slice(0, 1) + "***@" + parts[1];
    },
  },

  created() {
    this.email =
      lowercaseString(this.$route.query.email) ||
      localStorage.getItem("pendingLoginEmail") ||
      "";

    if (!this.email) {
      this.$store.commit("toast/NEW", {
        type: "error",
        message: "Missing email. Please login again.",
      });
      this.$router.replace({ name: "KioskLogin" });
    }

    this.startTimer();
  },

  methods: {
    startTimer() {
      this.timer = 60;

      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      this.timerInterval = setInterval(() => {
        if (this.timer > 0) {
          this.timer -= 1;
        } else {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
      }, 1000);
    },
    // Auto move to next box
    handleInput(event, index) {
      let value = event.target.value.replace(/\D/g, "");

      this.otpBoxes[index] = value;

      // iOS autofill case (entire OTP comes in first box)
      if (value.length === this.otpBoxes.length) {
        value.split("").forEach((d, i) => {
          this.otpBoxes[i] = d;
        });

        this.$nextTick(() => this.verifyOtp());
        return;
      }

      // move to next input
      if (value && index < this.otpBoxes.length - 1) {
        this.$refs.otpRefs[index + 1].focus();
      }

      // auto submit on last digit
      if (index === this.otpBoxes.length - 1 && value) {
        this.$nextTick(() => this.verifyOtp());
      }
    },

    // Move back on delete
    handleBackspace(event, index) {
      if (!this.otpBoxes[index] && index > 0) {
        this.$refs.otpRefs[index - 1].focus();
      }
    },

    // Paste full code
    handlePaste(event) {
      const text = event.clipboardData.getData("text").trim();

      if (!/^\d+$/.test(text)) return;

      event.preventDefault();

      text.split("").forEach((d, i) => {
        if (i < this.otpBoxes.length) {
          this.otpBoxes[i] = d;
        }
      });

      this.$nextTick(() => {
        if (text.length >= this.otpBoxes.length) {
          this.verifyOtp();
        } else {
          this.$refs.otpRefs[text.length]?.focus();
        }
      });
    },

    async verifyOtp() {
      this.fieldErrors = {};

      if (this.otp.length !== 6) {
        this.fieldErrors.otp = "Please enter a valid 6-digit OTP.";
        return;
      }

      this.isLoading = true;

      try {
        const resp = await AuthService.verifyLoginOtp({
          email: lowercaseString(this.email),
          otp: this.otp,
        });

        if (resp.success) {
          localStorage.setItem("token", resp?.data?.token || "");
          this.resetOtp();

          const hospitalId = resp?.data?.user?.hospitalId;
          if (hospitalId) localStorage.setItem("hospitalId", hospitalId);

          this.$store.commit("user/SET_CURRENT_USER", resp?.data || {});
          // this.$store.commit("toast/NEW", {
          //   type: "success",
          //   message: "Logged in successfully!",
          // });

          localStorage.removeItem("pendingLoginEmail");
          
          const userRole = resp?.data?.user?.objectType;
          if (["superadmin", "super_admin", "super-admin"].includes(userRole)) {
            this.$router.push("/super-admin-selection");
          } else {
            this.$router.push("/");
          }
          return;
        }

        this.$store.commit("toast/NEW", {
          type: "error",
          message: resp.message || "OTP verification failed.",
        });
      } catch (err) {
        handleError(err, this.fieldErrors, this.$store);
      } finally {
        this.isLoading = false;
        this.resetOtp();
      }
    },
    resetOtp() {
      this.otpBoxes = ["", "", "", "", "", ""];

      this.$nextTick(() => {
        const first = this.$refs.otpRefs?.[0];
        if (first) first.focus();
      });
    },

    async resendOtp() {
      if (!this.email) return;

      this.resendLoading = true;

      try {
        // const resp = await AuthService.resendOTP({ email: this.email });
        const resp = await AuthService.resendLoginOTP({ email: lowercaseString(this.email) });

        if (resp.success) {
          this.$store.commit("toast/NEW", {
            type: "success",
            message: "OTP resent to your email.",
          });

          this.startTimer(); // 🔥 Restart timer here
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: resp.message || "Failed to resend OTP.",
          });
        }
      } catch (err) {
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "Error resending OTP.",
        });
      } finally {
        this.resendLoading = false;
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      const first = this.$refs.otpRefs?.[0];
      if (first) first.focus();
    });
  }
};
</script>
<style scoped>
.otp-box-container {
  display: flex;
  justify-content: space-between;
  /* makes all 6 boxes fill the full row */
  /* gap: 10px; */
  /* space between boxes */
  width: 100%;
  /* full width */
  margin: 20px 0;
}

.otp-box {
  flex: 1;
  /* each box grows evenly */
  /* max-width: 60px; */
  /* optional: limits box size on wide screens */
  height: 60px;
  text-align: center;
  font-size: 24px;
  border: 1.5px solid #ccc;
  border-radius: 16px;
  outline: none;
  font-weight: bold;
  background: #fff;
}

.otp-box:focus {
  border-color: #007bff;
  box-shadow: 0 0 4px rgba(0, 123, 255, 0.4);
}

.otp-footer {
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  justify-content: center;
}

.otp-resend,
.otp-change-email {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
}

.otp-divider {
  color: #777;
}

.otp-box-container {
  display: flex;
  justify-content: center;
  /* changed from space-between to center */
  gap: 8px;
  /* reduced gap between boxes */
  width: 100%;
  margin: 20px 0;
}

.otp-box {
  width: 70px;
  max-width: 70px;

  height: 70px;
  text-align: center;
  font-size: 24px;
  border: 1.5px solid #ccc;
  border-radius: 16px;
  outline: none;
  font-weight: bold;
  background: #fff;
}

.otp-box:focus {
  border: 2px solid #0368d3;
  box-shadow: 0 0 4px rgba(0, 123, 255, 0.597);
}





/*  */

.kl--container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.heading-kl {
  font-size: 32px;
  font-weight: 600;
  text-align: center;
}


.heading-info {
  font-size: 20px;
  color: #666;
  margin-top: -40px;
  margin-bottom: 40px;
}


/* Submit button styling with proper disabled state */
.login-submit-button {
  width: 100%;
  max-width: 300px;
  margin: 20px auto 0 auto;
  display: block;
  padding: 14px 24px;
  font-size: 18px;
  font-weight: 600;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.login-submit-button:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.login-submit-button:disabled {
  background: #0056b3;
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

/* Error message styling */
.field-error {
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 0;
  text-align: center;
}
</style>
